import React, { useState } from 'react';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, RadialBarChart, RadialBar } from 'recharts';
import { TrendingUp, TrendingDown, AlertTriangle, Target, Brain, Zap } from 'lucide-react';
import { motion } from 'framer-motion';

const PredictiveAnalytics = () => {
  const { predictiveData, loading } = useAnalytics();
  const [selectedTimeframe, setSelectedTimeframe] = useState('quarterly');
  const [selectedMetric, setSelectedMetric] = useState('all');

  const timeframeOptions = [
    { value: 'monthly', label: 'Next 3 Months' },
    { value: 'quarterly', label: 'Next 4 Quarters' },
    { value: 'yearly', label: 'Next 2 Years' }
  ];

  const metricOptions = [
    { value: 'all', label: 'All Metrics' },
    { value: 'goals', label: 'Goal Performance' },
    { value: 'okrs', label: 'OKR Scores' },
    { value: 'performance', label: 'Employee Performance' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Generating predictive insights...</p>
        </div>
      </div>
    );
  }

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.8) return 'High Confidence';
    if (confidence >= 0.6) return 'Medium Confidence';
    return 'Low Confidence';
  };

  const getRiskSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Brain className="mr-3 text-purple-600" size={28} />
            Predictive Analytics
          </h2>
          <p className="text-gray-600 mt-1">AI-powered insights and performance forecasting</p>
        </div>
        
        <div className="flex space-x-4">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {timeframeOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {metricOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Key Predictions Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Goal Completion Prediction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <Target className="text-blue-600" size={24} />
            <span className={`text-sm font-medium ${getConfidenceColor(predictiveData.goalCompletion?.confidence)}`}>
              {getConfidenceLabel(predictiveData.goalCompletion?.confidence)}
            </span>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Goal Completion Forecast</h3>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Current Rate:</span>
              <span className="font-bold text-gray-900">{predictiveData.goalCompletion?.currentRate}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Projected Rate:</span>
              <div className="flex items-center">
                <span className="font-bold text-blue-600">{predictiveData.goalCompletion?.projectedRate}%</span>
                {(predictiveData.goalCompletion?.projectedRate || 0) > (predictiveData.goalCompletion?.currentRate || 0) ? 
                  <TrendingUp className="ml-1 text-green-500" size={16} /> : 
                  <TrendingDown className="ml-1 text-red-500" size={16} />
                }
              </div>
            </div>
          </div>
        </motion.div>

        {/* OKR Performance Prediction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <Zap className="text-indigo-600" size={24} />
            <span className="text-sm font-medium text-green-600">High Confidence</span>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">OKR Score Projection</h3>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Current Score:</span>
              <span className="font-bold text-gray-900">{Math.round((predictiveData.okrProjections?.currentScore || 0) * 100)}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Next Quarter:</span>
              <div className="flex items-center">
                <span className="font-bold text-indigo-600">
                  {Math.round((predictiveData.okrProjections?.quarterlyProjections?.[0]?.projected_score || 0) * 100)}%
                </span>
                <TrendingUp className="ml-1 text-green-500" size={16} />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Performance Trend Prediction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="text-green-600" size={24} />
            <span className="text-sm font-medium text-green-600">Medium Confidence</span>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Performance Improvement</h3>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Current Avg:</span>
              <span className="font-bold text-gray-900">
                {Math.round((predictiveData.performanceTrends?.current_performance || 0) * 100)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Predicted:</span>
              <div className="flex items-center">
                <span className="font-bold text-green-600">
                  {Math.round((predictiveData.performanceTrends?.predicted_improvement || 0) * 100)}%
                </span>
                <TrendingUp className="ml-1 text-green-500" size={16} />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Goal Completion Trend Chart */}
      {(selectedMetric === 'all' || selectedMetric === 'goals') && predictiveData.goalCompletion?.monthlyTrends && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Goal Completion Trend & Forecast</h3>
          
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={predictiveData.goalCompletion.monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="completion_rate" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={(props) => {
                  const { payload } = props;
                  return (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={4}
                      fill={payload.prediction ? "#F59E0B" : "#3B82F6"}
                      stroke={payload.prediction ? "#F59E0B" : "#3B82F6"}
                      strokeWidth={2}
                    />
                  );
                }}
                name="Completion Rate (%)"
              />
            </LineChart>
          </ResponsiveContainer>
          
          <div className="mt-4 flex items-center space-x-6 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span>Historical Data</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
              <span>Predicted Values</span>
            </div>
          </div>
        </motion.div>
      )}

      {/* OKR Quarterly Projections */}
      {(selectedMetric === 'all' || selectedMetric === 'okrs') && predictiveData.okrProjections?.quarterlyProjections && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">OKR Quarterly Projections</h3>
          
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={predictiveData.okrProjections.quarterlyProjections}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="quarter" />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => [
                  name === 'projected_score' ? `${Math.round(value * 100)}%` : value,
                  name === 'projected_score' ? 'Projected Score' : 'Objectives at Risk'
                ]}
              />
              <Legend />
              <Bar dataKey="projected_score" fill="#6366F1" name="Projected Score" />
              <Bar dataKey="objectives_at_risk" fill="#EF4444" name="Objectives at Risk" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Risk Assessment */}
      {predictiveData.riskAssessment && predictiveData.riskAssessment.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="flex items-center mb-4">
            <AlertTriangle className="text-orange-500 mr-2" size={20} />
            <h3 className="text-lg font-semibold text-gray-900">Risk Assessment</h3>
          </div>
          
          <div className="space-y-4">
            {predictiveData.riskAssessment.map((risk, index) => (
              <div key={index} className={`border rounded-lg p-4 ${getRiskSeverityColor(risk.severity)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="font-semibold text-sm uppercase tracking-wide">
                        {risk.severity} Risk
                      </span>
                    </div>
                    <h4 className="font-medium mb-1">{risk.description}</h4>
                    <p className="text-sm mb-2">{risk.impact}</p>
                    <div className="text-sm font-medium">
                      Recommendation: <span className="font-normal">{risk.recommendation}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* AI Recommendations */}
      {predictiveData.recommendations && predictiveData.recommendations.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="flex items-center mb-4">
            <Brain className="text-purple-500 mr-2" size={20} />
            <h3 className="text-lg font-semibold text-gray-900">AI-Powered Recommendations</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {predictiveData.recommendations.map((recommendation, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${
                    recommendation.priority === 'high' ? 'bg-red-500' :
                    recommendation.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}></div>
                  
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">{recommendation.title}</h4>
                    <p className="text-sm text-gray-600 mb-2">{recommendation.description}</p>
                    <div className="text-sm font-medium text-blue-600">{recommendation.action}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Prediction Factors */}
      {predictiveData.goalCompletion?.factors && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gray-50 rounded-lg p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Prediction Methodology</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Factors Considered:</h4>
              <ul className="space-y-1">
                {predictiveData.goalCompletion.factors.map((factor, index) => (
                  <li key={index} className="text-sm text-gray-600 flex items-center">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                    {factor}
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Confidence Level:</h4>
              <div className="flex items-center mb-2">
                <div className="flex-1 bg-gray-200 rounded-full h-3 mr-3">
                  <div 
                    className="bg-green-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${(predictiveData.goalCompletion.confidence || 0) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {Math.round((predictiveData.goalCompletion.confidence || 0) * 100)}%
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Based on historical data patterns and current performance indicators
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PredictiveAnalytics;